import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";

import { organisationService } from "../../../services/organisation.service";
import { IOrganizationRole } from "../createorganisation/interfaces/organization-requested-members.interface";
import { Toast } from "../../../utils/Toast";

interface IProps {
    organizationId: string;
    organizationName?: string;
    /** Called with the chosen role ids. Resolves once the save has landed. */
    onSave: (roleIds: string[]) => Promise<void>;
    onCancel: () => void;
}

/**
 * Picks which roles of one organization must use a second factor.
 *
 * Shown when a platform admin switches 2FA on, and reachable afterwards to
 * change the selection. At least one role is required — the API refuses an
 * empty list, because "2FA on, nobody protected" is not a state the switch can
 * express.
 */
export const TwoFactorRolesModal: React.FC<IProps> = ({
    organizationId,
    organizationName,
    onSave,
    onCancel,
}) => {
    const { t } = useTranslation('', { keyPrefix: 'AdminDashboardOrganizations' });
    const [roles, setRoles] = useState<IOrganizationRole[]>([]);
    const [selected, setSelected] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const res: any = await organisationService.getOrganizationRoles(organizationId);
                const organizationRoles: IOrganizationRole[] =
                    res?.payload?.organizationRoles ?? [];
                setRoles(organizationRoles);
                // Pre-tick whatever is already flagged. A first-time setup has
                // nothing flagged yet, so every role starts selected — which is
                // what the organization switch used to mean on its own.
                const alreadyFlagged = organizationRoles.filter(
                    (role) => role.isTwoFactorEnabled
                );
                setSelected(
                    (alreadyFlagged.length ? alreadyFlagged : organizationRoles).map(
                        (role) => role._id
                    )
                );
            } catch (error: any) {
                Toast.fire({
                    icon: "warning",
                    title: error?.response?.data?.message || t('couldNotLoadRoles'),
                });
                onCancel();
            } finally {
                setIsLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    const toggleRole = (roleId: string) =>
        setSelected((current) =>
            current.includes(roleId)
                ? current.filter((id) => id !== roleId)
                : [...current, roleId]
        );

    const save = async () => {
        if (selected.length === 0) return;
        setIsSaving(true);
        try {
            await onSave(selected);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal className="up-thememodal up-modalreport" show={true} onHide={onCancel}>
            <Modal.Header className="up-modalheader" closeButton>
                <Modal.Title>{t('twoFactorRoles')}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="up-modalbody">
                <div className="up-modalcontentholder">
                    <div className="up-description">
                        <p>
                            {organizationName
                                ? `${t('selectRolesForTwoFactor')} — ${organizationName}`
                                : t('selectRolesForTwoFactor')}
                        </p>
                    </div>

                    {isLoading && <p>{t('loadingRoles')}</p>}

                    {!isLoading && roles.length === 0 && <p>{t('noRolesToConfigure')}</p>}

                    {!isLoading &&
                        roles.map((role) => (
                            <div className="form-group up-twofamodalcheckbox " key={role._id}>
                                <label htmlFor={`up-2farole-${role._id}`}>{role.role}</label>
                                <div className="up-checkbox up-remebermearea">
                                    <input
                                        type="checkbox"
                                        id={`up-2farole-${role._id}`}
                                        checked={selected.includes(role._id)}
                                        onChange={() => toggleRole(role._id)}
                                    />
                                    <label htmlFor={`up-2farole-${role._id}`}></label>
                                </div>
                            </div>
                        ))}

                    {!isLoading && roles.length > 0 && selected.length === 0 && (
                        <small className="form-text text-muted">{t('atLeastOneRole')}</small>
                    )}

                    <button
                        type="button"
                        className="up-btn up-btnwithloader"
                        disabled={isLoading || isSaving || selected.length === 0}
                        onClick={save}
                    >
                        {t('save')} {isSaving && <div className="lds-dual-ring"></div>}
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default TwoFactorRolesModal;
